import { supabase } from '../config/supabase';

/**
 * Ensure we have a valid session before calling functions
 */
const ensureSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    console.error('Error getting session:', error);
  }
  if (!session) {
    throw new Error('Not authenticated. Please log in to access analytics.');
  }
  return session;
};

/**
 * Fetch page views over time from Google Analytics
 * @param {number} days - Number of days to fetch (7, 30, or 90)
 * @returns {Promise<Array>} Array of {date, views} objects
 */
export const fetchPageViews = async (days = 7) => {
  try {
    // Ensure we have a valid session
    await ensureSession();
    
    const { data, error } = await supabase.functions.invoke('analytics-page-views', {
      body: { days },
    });

    // Even if there's an error, data might contain the error message
    if (data && !data.success) {
      throw new Error(data.error || data.details || 'Failed to fetch page views');
    }

    if (error) {
      // Try to extract error from response body
      let errorMessage = 'Failed to fetch page views';
      if (data?.error) {
        errorMessage = data.error;
      } else if (data?.details) {
        errorMessage = data.details;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.context?.body?.error) {
        errorMessage = error.context.body.error;
      }
      throw new Error(errorMessage);
    }

    return data?.data || [];
  } catch (error) {
    console.error('Error fetching page views:', error);
    throw error;
  }
};

/**
 * Real website page paths (marketvendr-website routes). Used to filter out noise from
 * Popular Pages: bots, typos (e.g. /ditavoneese), crawler probes (/befhbef), and
 * translation/proxy URLs (/_t/c/v3/... long strings) that GA4 records but aren't real pages.
 */
const KNOWN_PAGE_PATHS = ['/', '/about', '/vendrs', '/contact'];

function isRealPagePath(path) {
  if (!path || typeof path !== 'string') return false;
  const normalized = path.split('?')[0].replace(/\/$/, '') || '/';
  if (normalized.startsWith('/_t/') || path.length > 100) return false;
  return KNOWN_PAGE_PATHS.includes(normalized);
}

/**
 * Fetch most popular pages from Google Analytics (only real site pages; filters out
 * translation URLs, long encoded paths, and unknown/bot paths).
 * @param {number} days - Number of days to fetch
 * @returns {Promise<Array>} Array of {page, views} objects
 */
export const fetchPopularPages = async (days = 7) => {
  try {
    await ensureSession();

    const { data, error } = await supabase.functions.invoke('analytics-popular-pages', {
      body: { days },
    });

    if (data && !data.success) {
      throw new Error(data.error || data.details || 'Failed to fetch popular pages');
    }

    if (error) {
      const errorMessage = data?.error || data?.details || error.message || 'Failed to fetch popular pages';
      throw new Error(errorMessage);
    }

    const raw = data?.data || [];
    return raw.filter((p) => isRealPagePath(p.page));
  } catch (error) {
    console.error('Error fetching popular pages:', error);
    throw error;
  }
};

/**
 * Fetch top countries from Google Analytics
 * @param {number} days - Number of days to fetch
 * @returns {Promise<Array>} Array of {name, visitors, color} objects
 */
export const fetchTopCountries = async (days = 7) => {
  try {
    await ensureSession();
    
    const { data, error } = await supabase.functions.invoke('analytics-countries', {
      body: { days },
    });

    if (data && !data.success) {
      throw new Error(data.error || data.details || 'Failed to fetch countries');
    }

    if (error) {
      const errorMessage = data?.error || data?.details || error.message || 'Failed to fetch countries';
      throw new Error(errorMessage);
    }

    return data?.data || [];
  } catch (error) {
    console.error('Error fetching countries:', error);
    throw error;
  }
};

/**
 * Fetch device breakdown from Google Analytics
 * @param {number} days - Number of days to fetch
 * @returns {Promise<Array>} Array of {name, value, color} objects
 */
export const fetchDevices = async (days = 7) => {
  try {
    await ensureSession();
    
    const { data, error } = await supabase.functions.invoke('analytics-devices', {
      body: { days },
    });

    if (data && !data.success) {
      throw new Error(data.error || data.details || 'Failed to fetch devices');
    }

    if (error) {
      const errorMessage = data?.error || data?.details || error.message || 'Failed to fetch devices';
      throw new Error(errorMessage);
    }

    return data?.data || [];
  } catch (error) {
    console.error('Error fetching devices:', error);
    throw error;
  }
};

/**
 * Fetch summary statistics from Google Analytics
 * @param {number} days - Number of days to fetch
 * @returns {Promise<Object>} Object with totalViews, uniqueUsers, avgDailyViews, peakDay
 */
export const fetchSummary = async (days = 7) => {
  try {
    await ensureSession();
    
    const { data, error } = await supabase.functions.invoke('analytics-summary', {
      body: { days },
    });

    if (data && !data.success) {
      throw new Error(data.error || data.details || 'Failed to fetch summary');
    }

    if (error) {
      const errorMessage = data?.error || data?.details || error.message || 'Failed to fetch summary';
      throw new Error(errorMessage);
    }

    return data?.data || {};
  } catch (error) {
    console.error('Error fetching summary:', error);
    throw error;
  }
};

